import {
  GetHeaderResponse_TargetGroupItem,
  GetHeaderResponse_TargetGroupSwitch,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/brand_hub/v1/service.types';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  darkTheme,
} from '@aboutyou/ui-components';
import React from 'react';
import styled, {css} from 'styled-components';
import {Link} from '../../../features/Link/Link';
import {useSelectedTargetGroup} from '../../../shared-deprecated/useSelectedTargetGroup';
import {useTargetGroupIdentifier} from '../../../shared-deprecated/useTargetGroupIdentifier';

export interface BrandHubGenderSwitchViewProps {
  className?: string;
  forDiscover?: boolean;
  standalone?: boolean;
}

export const BrandHubGenderSwitchView: React.FC<
  BrandHubGenderSwitchViewProps & {
    targetGroups: GetHeaderResponse_TargetGroupItem[];
    targetGroupSwitch?: GetHeaderResponse_TargetGroupSwitch;
  }
> = ({
  targetGroups = [],
  targetGroupSwitch,
  className,
  forDiscover,
  standalone = false,
}) => {
  const targetGroupIdentifier = useTargetGroupIdentifier();
  const {targetGroup: sessionTargetGroup} = useSelectedTargetGroup();
  const targetGroup = forDiscover ? targetGroupIdentifier : sessionTargetGroup;

  const activeTargetGroup = targetGroupSwitch?.targetGroup ?? targetGroup;

  const targetGroupsToRender = forDiscover
    ? targetGroups.filter(targetGroup => targetGroup.isDiscoverEnabled)
    : targetGroups;

  if (targetGroupsToRender.length < 2) {
    return null;
  }

  return (
    <Wrapper
      className={className}
      $standalone={standalone}
      data-testid="brandGenderSwitch"
    >
      {targetGroupsToRender.map(targetGroup => (
        <StyledGender
          key={targetGroup.targetGroup}
          label={targetGroup.label}
          linkTarget={
            forDiscover ? targetGroup.discoverPath! : targetGroup.shopPath
          }
          data-active={targetGroup.targetGroup === activeTargetGroup}
        />
      ))}
    </Wrapper>
  );
};

const Wrapper = styled.section<{$standalone: boolean}>`
  ${({$standalone}) =>
    $standalone &&
    css`
      display: flex;
      justify-content: center;
      height: 36px;
      box-sizing: border-box;
      border-bottom: 1px solid ${COLORS.Grey3};

      ${darkTheme(css`
        border-color: #333333;
      `)}
    `}
`;

const StyledGender = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  min-width: 52px;
  height: 100%;
  box-sizing: content-box;
  color: ${COLORS.Grey1};
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.bold};
  line-height: 18px;
  transition: color 0.5s;
  text-decoration: none;
  text-transform: uppercase;

  ${darkTheme(css`
    color: ${COLORS.Grey2};
  `)}

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    margin: auto;
    width: 24px;
    height: 2px;
    background: ${COLORS.Black};
    transform: scale(0);
    transition: transform 0.5s ease;

    ${darkTheme(css`
      background: ${COLORS.White};
    `)}
  }

  &[data-active='true'] {
    color: ${COLORS.Black};
    font-weight: ${FONT_WEIGHTS.heavy};

    &::after {
      transform: scale(1);
    }

    ${darkTheme(css`
      color: ${COLORS.White};
    `)}
  }

  &:hover {
    color: ${COLORS.Black};

    ${darkTheme(css`
      color: ${COLORS.White};
    `)}
  }
`;
